"use client";

import { clsx } from "@trydig/ui";
import { Suspense, useEffect, useId, useRef } from "react";
import { generateVariantReviewsIdentifiers } from "@/features/reviews/utils";
import { useTranslations } from "@/i18n/client";
import { useGeo } from "@/i18n/routing";
import type { ProductCard, ProductDetails } from "@/lib/centra";
import { REVIEWS_STORE_ID } from "./constants";
import { ProductRating } from "./ProductRating";

const RUK_RATING_SNIPPET = "ruk_rating_snippet";

let scriptLoaded = false;
let scriptLoading = false;
let cssLoaded = false;
const initQueue: Array<() => void> = [];

export const useReviewsScript = () => {
	const scriptReadyRef = useRef(false);

	useEffect(() => {
		if (!cssLoaded) {
			const link = document.createElement("link");
			link.rel = "stylesheet";
			link.href = "https://widget.reviews.io/rating-snippet/dist.css";
			document.head.appendChild(link);
			cssLoaded = true;
		}

		if (!scriptLoaded && !scriptLoading) {
			scriptLoading = true;
			const script = document.createElement("script");
			script.src = "https://widget.reviews.io/rating-snippet/dist.js";
			script.onload = () => {
				scriptLoaded = true;
				scriptLoading = false;
				scriptReadyRef.current = true;

				initQueue.forEach((fn) => fn());
				initQueue.length = 0;
			};
			document.head.appendChild(script);
		} else if (scriptLoaded) {
			scriptReadyRef.current = true;
		}
	}, []);

	const initializeSnippet = (callback: () => void) => {
		if (scriptReadyRef.current && window.ratingSnippet) {
			callback();
		} else {
			initQueue.push(callback);
		}
	};

	return { initializeSnippet, isReady: scriptReadyRef.current };
};

export interface RatingSnippetProps {
	product: ProductDetails | ProductCard | string;
	variant?: "default" | "minimal";
	color?: string;
	linebreak?: boolean;
	displayPopupOnClick?: boolean;
	showEmptyStars?: boolean;
	className?: string;
}

const UnsuspendedRatingSnippet = ({
	product,
	variant = "default",
	color = "#0E1311",
	linebreak = false,
	displayPopupOnClick = true,
	showEmptyStars = false,
	className,
}: RatingSnippetProps) => {
	const t = useTranslations();
	const geo = useGeo();
	const id = useId();
	const { initializeSnippet } = useReviewsScript();

	const productIds =
		typeof product === "string"
			? [product]
			: generateVariantReviewsIdentifiers(product);

	useEffect(() => {
		const snippetId = RUK_RATING_SNIPPET + id;

		initializeSnippet(() => {
			if (typeof window !== "undefined" && window.ratingSnippet) {
				window.ratingSnippet(snippetId, {
					store: REVIEWS_STORE_ID,
					mode: variant,
					color,
					linebreak,
					text: t("Reviews"),
					singularText: t("Review"),
					lang: geo.language === "nb" ? "no" : geo.language,
					usePolaris: displayPopupOnClick,
					showEmptyStars,
				});
			}
		});
	}, [
		variant,
		color,
		linebreak,
		displayPopupOnClick,
		showEmptyStars,
		t,
		geo,
		id,
		initializeSnippet,
	]);

	return (
		<div style={{ position: "relative" }}>
			<div
				className={clsx(RUK_RATING_SNIPPET + id)}
				data-sku={productIds.join(";")}
				style={{
					visibility: "hidden",
					position: "absolute",
					pointerEvents: displayPopupOnClick ? "auto" : "none",
				}}
			/>
			<div
				className={className}
				onClick={() => {
					if (displayPopupOnClick) {
						const widget = document.querySelector(
							`.${RUK_RATING_SNIPPET + id}`,
						);
						if (widget instanceof HTMLElement) {
							widget.click();
						}
					}
				}}
				style={{ cursor: displayPopupOnClick ? "pointer" : "default" }}
			>
				<ProductRating productIds={productIds} variant={"extended"} />
			</div>
		</div>
	);
};

export const RatingSnippet: typeof UnsuspendedRatingSnippet = (props) => {
	return (
		<Suspense>
			<UnsuspendedRatingSnippet {...props} />
		</Suspense>
	);
};
